import { CONFIG } from "../../../config";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

const CRMCONFIG = CONFIG.modules["crm"];
const API_HOST_CRM = CRMCONFIG.host;

const isDevelopment = process.env.NODE_ENV === 'development';
const devLog = (message, data) => {
  if (isDevelopment) console.log(message, data);
};

/**
 * Hook to fetch daily call type analytics (answered vs notAnswered) for a month and a type
 * queries should include year, month and type
 */
/**
 * useGetDailyCallTypeAnalytics
 *
 * React hook to fetch daily call-type analytics (answered vs notAnswered) for a
 * specified month and call type. Wraps an axios GET request to the CRM analytics
 * endpoint and returns a React Query result object. The hook supports the same
 * options and error handling style as other analytics hooks.
 *
 * @param {Object} [queries={}] - Query parameters to pass to the API. Typical keys:
 *   - year {number} (required)
 *   - month {number} (1-12) (required)
 *   - type {string} (call type, e.g., 'InBound', 'Manual') (required)
 * @param {Object} [options={}] - React Query options / overrides (enabled, staleTime, cacheTime, retry, etc.)
 * @returns {Object} React Query result (see `useQuery` from @tanstack/react-query)
 * @example
 * const { data, isLoading, error } = useGetDailyCallTypeAnalytics({ year: 2025, month: 5, type: 'InBound' });
 */
export const useGetDailyCallTypeAnalytics = (queries = {}, options = {}) => {
  const validatedQueries = useMemo(() => {
    if (!queries || typeof queries !== 'object') {
      devLog('[useGetDailyCallTypeAnalytics] Invalid queries parameter, using empty object');
      return {};
    }
    return queries;
  }, [queries]);

  const queryOptions = useMemo(() => ({
    enabled: true,
    refetchOnWindowFocus: false,
    retry: 2,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
    ...options
  }), [options]);

  const queryKey = useMemo(() => {
    const sortedEntries = Object.entries(validatedQueries)
      .filter(([, value]) => value !== undefined && value !== null)
      .sort(([a], [b]) => a.localeCompare(b));

    return ["dailyCallTypeAnalytics", ...sortedEntries.flat()];
  }, [validatedQueries]);

  const fetchDailyCallTypeAnalytics = useCallback(async () => {
    try {
      const cleanParams = Object.fromEntries(
        Object.entries(validatedQueries).filter(([, value]) => value !== undefined && value !== null && value !== '')
      );

      devLog('[useGetDailyCallTypeAnalytics] Calling API with params:', cleanParams);

      const response = await axios.get(`${API_HOST_CRM}/analytics/daily-call-type-analytics`, { params: cleanParams, timeout: 30000 });

      devLog('[useGetDailyCallTypeAnalytics] Response:', response.data);

      if (!response.data) throw new Error('No data received from server');
      return response.data;
    } catch (error) {
      const isNetworkError = !error.response && error.code === 'ENOTFOUND';
      const isTimeout = error.code === 'ECONNABORTED';
      let msg = 'Error fetching daily call type analytics';
      if (isNetworkError) msg += ': Network error';
      else if (isTimeout) msg += ': Timeout';
      else if (error.response) msg += `: Server responded with ${error.response.status}`;

      console.error(msg, { error, params: validatedQueries, time: new Date().toISOString() });
      throw new Error(msg);
    }
  }, [validatedQueries]);

  return useQuery({
    queryKey,
    queryFn: fetchDailyCallTypeAnalytics,
    enabled: queryOptions.enabled,
    refetchOnWindowFocus: queryOptions.refetchOnWindowFocus,
    retry: queryOptions.retry,
    staleTime: queryOptions.staleTime,
    cacheTime: queryOptions.cacheTime,
    onError: (error) => console.error('useGetDailyCallTypeAnalytics error', { error: error.message, queries: validatedQueries }),
    select: (data) => data || null,
  });
};
